import { ADMIN_ROLE } from '@careapp-group/core';
import { createSelector } from '@ngrx/store';
import {
  RecoveryFlow,
  RecoveryFlowState,
  RegistrationFlow,
  Session,
  SuccessfulNativeRegistration,
  VerificationFlow,
} from '@ory/client';
import {
  CAAction,
  REQUEST_CUSTOM_PASSWORD_RESET_ACTION,
  RESPONSE_FAIL_CUSTOM_PASSWORD_RESET_ACTION,
} from '../actions';
import * as actions from '../actions/index';
import { MemberDto, VerificationResponseDto } from '@careapp-group/ng-sdk-v2-new';
import { environment } from '../../../environments/environment';

export const LOCAL_STORAGE_AUTH_KEY = 'careapp.auth';

export interface KratosAuth {
  session: Session;
  userId: string;
}
export interface LoopbackAuth {
  id: string;
  ttl: number;
  created: Date;
  userId: string;
  dirty?: boolean;
  user: MemberDto;
}

export interface State {
  loading: boolean;
  resetPassword: boolean;
  error?: string;
  auth?: KratosAuth | LoopbackAuth;
  user?: MemberDto;
  roles?: Array<string>;
  ready: boolean;
  accountRecoveryFlow?: RecoveryFlow;
  registrationFlow?: RegistrationFlow | 'complete';
  verificationFlow?: VerificationFlow;
  currentVerification?: VerificationResponseDto;
  currentPasswordResetToken?: {
    id: string;
    ttl: number;
    created: Date;
    userId: string;
    // Not sent from server.
    used?: boolean;
  };
  requestingVerification: boolean;
  requestingPasswordResetToken: boolean;
  passwordChangeFlowId?: string;
  passwordChangeFlow?: 'IN_PROGRESS' | 'COMPLETE';
  postLoginRoute?: string;
}

export const initialState = {
  loading: false,
  resetPassword: false,
  error: null,
  ready: false,
  requestingVerification: false,
  requestingPasswordResetToken: false,
  passwordChangeFlow: null,
};

export function reducer(state: State = initialState, action: any): State {
  switch (action.type) {
    case actions.REQUEST_AUTH_LOGIN:
    case actions.REQUEST_LOGGED_IN_USER:
    case actions.RequestChangePasswordAction.type:
    case actions.RequestSaveProfile.type:
    case actions.REQUEST_AUTH_FORGOT_PASSWORD:
    case actions.REQUEST_SET_PHONE_NUMBER:
    case REQUEST_CUSTOM_PASSWORD_RESET_ACTION: {
      return { ...state, loading: true, resetPassword: false };
    }
    case actions.REQUEST_VERIFICATION: {
      return { ...state, loading: true, requestingVerification: true };
    }

    case actions.CLEAR_VERIFICATION: {
      return { ...state, requestingVerification: false, currentVerification: null };
    }

    case actions.REQUEST_PASSWORD_RESET_TOKEN: {
      return { ...state, loading: true, requestingPasswordResetToken: true };
    }

    case actions.AUTH_RESET_ERRORS: {
      return { ...state, error: '', resetPassword: false };
    }

    case actions.RequestAuthLogoutAction.type: {
      return { ...state, auth: null, loading: true };
    }

    case actions.RESPONSE_SUCCESS_AUTH_FORGOT_PASSWORD: {
      return {
        ...state,
        auth: null,
        loading: false,
        resetPassword: true,
        error: '',
      };
    }
    case actions.RESPONSE_SUCCESS_UPDATED_PROFILE:
    case actions.ResponseSuccessSaveProfile.type: {
      return { ...state, user: action.payload, loading: false };
    }

    case actions.RESPONSE_SUCCESS_LOGGED_IN_USER: {
      return { ...state, user: action.payload, ready: true };
    }

    case actions.APPLICATION_AUTHORIZED: {
      if (action.payload.user) {
        return { ...state, user: action.payload.user, ready: true, loading: false };
      }
      return {
        ...state,
        ready: true,
      };
    }

    case actions.RESPONSE_SUCCESS_USER_ROLES:
      return { ...state, roles: action.payload };

    case actions.ResponseSuccessChangePasswordAction.type: {
      return {
        ...state,
        loading: false,
        error: '',
        passwordChangeFlow: 'COMPLETE',
        passwordChangeFlowId: null,
      };
    }
    case actions.ResponseFailChangePasswordAction.type: {
      return { ...state, loading: false, error: (<any>action).error };
    }
    case actions.BeginPasswordChange.type: {
      return { ...state, passwordChangeFlow: 'IN_PROGRESS' };
    }

    case actions.APPLICATION_SESSION_READY:
    case actions.RESPONSE_SUCCESS_AUTH_REFRESH:
    case actions.RESPONSE_SUCCESS_AUTH_LOGIN: {
      if (environment.useKratos) {
        if (action.type !== actions.RESPONSE_SUCCESS_AUTH_REFRESH) {
          return {
            ...state,
            auth: {
              session: (<any>action).session,
              userId: (<any>action).userId,
            },
          };
        } else {
          return state;
        }
      } else {
        const newState = {
          ...state,
          loading: false,
          error: '',
          auth: {
            ...action.payload,
            created: new Date(),
            dirty: false,
          },
        };
        return newState;
      }
    }

    case actions.HYDRATE_AUTH: {
      return {
        ...state,
        auth: {
          ...(state.auth || {}),
          ...action.payload,
          dirty: true,
          date: new Date(),
        },
      };
    }

    case actions.RequestUpdateMemberMetaAction.type: {
      return { ...state, loading: true };
    }

    case actions.ResponseFailUpdateMemberMetaAction.type: {
      return { ...state, loading: false, error: action.payload };
    }

    case actions.ResponseSuccessUpdateMemberMetaAction.type: {
      const user = { ...state.user };
      user.meta = action.payload;
      return { ...state, auth: { ...state.auth }, user, loading: false };
    }

    case actions.RESPONSE_FAIL_SET_PHONE_NUMBER:
    case actions.RESPONSE_FAIL_SET_EMAIL_ADDRESS:
    case actions.RESPONSE_FAIL_PASSWORD_RESET_TOKEN:
    case actions.RESPONSE_FAIL_VERIFICATION:
    case RESPONSE_FAIL_CUSTOM_PASSWORD_RESET_ACTION: {
      return {
        ...state,
        loading: false,
        requestingPasswordResetToken: false,
        requestingVerification: false,
        error: action.payload,
      };
    }

    case actions.RESPONSE_SUCCESS_VERIFICATION: {
      return {
        ...state,
        currentVerification: action.payload,
        loading: false,
        requestingVerification: false,
        error: '',
      };
    }

    case actions.RESPONSE_SUCCESS_PASSWORD_RESET_TOKEN: {
      return {
        ...state,
        currentPasswordResetToken: { ...action.payload, used: false },
        loading: false,
        requestingPasswordResetToken: false,
        error: '',
      };
    }

    case actions.RESPONSE_SUCCESS_CUSTOM_PASSWORD_RESET_ACTION: {
      return {
        ...state,
        currentPasswordResetToken: { ...state.currentPasswordResetToken, used: true },
        loading: false,
        error: '',
      };
    }

    case actions.RESPONSE_SUCCESS_SET_PHONE_NUMBER: {
      const updatedVerification = { ...state.currentVerification };
      updatedVerification.status = 'complete';
      return {
        ...state,
        user: action.payload,
        currentVerification: updatedVerification,
        loading: false,
      };
    }

    case actions.RESPONSE_SUCCESS_SET_EMAIL_ADDRESS: {
      const updatedVerification = { ...state.currentVerification };
      updatedVerification.status = 'complete';
      return {
        ...state,
        user: action.payload,
        currentVerification: updatedVerification,
        loading: false,
      };
    }

    case actions.RESPONSE_FAIL_AUTH_LOGOUT:
    case actions.ResponseSuccessAuthLogoutAction.type: {
      return initialState;
    }

    case actions.RESPONSE_FAIL_AUTH_LOGIN:
    case actions.RESPONSE_FAIL_AUTH_FORGOT_PASSWORD: {
      const error = action.payload.code || action.payload;
      return { ...state, auth: null, loading: false, error };
    }

    case actions.ClearRecoveryFlow.type: {
      return { ...state, loading: false, error: null, accountRecoveryFlow: null };
    }
    case actions.RequestBeginAccountRecovery.type:
      return { ...state, loading: true };

    case actions.SuccessBeginAccountRecovery.type:
      return { ...state, loading: false, accountRecoveryFlow: (<any>action).flow };

    case actions.RequestCompleteAccountRecovery.type:
      return { ...state, loading: true };

    case actions.FailCompleteAccountRecovery.type:
      return { ...state, loading: false, error: (<any>action).error };

    case actions.SuccessCompleteAccountRecovery.type:
      return {
        ...state,
        loading: false,
        error: null,
        accountRecoveryFlow: {
          ...state.accountRecoveryFlow,
          state: RecoveryFlowState.PassedChallenge,
        },
        passwordChangeFlowId: (<any>action).passwordChangeFlowId,
      };

    case actions.RequireLogin.type:
    case actions.RequireRegistration.type:
      return { ...state, postLoginRoute: (<any>action).postLoginRoute };

    case actions.RequestBeginRegistrationFlow.type:
      return { ...state, loading: true };

    case actions.SuccessBeginRegistrationFlow.type:
      return { ...state, loading: false, registrationFlow: (<any>action).flow };

    case actions.FailBeginRegistrationFlow.type:
      return { ...state, loading: false, error: (<any>action).error };

    case actions.RequestCompleteRegistrationFlow.type:
      return { ...state, loading: true };

    case actions.SuccessCompleteRegistrationFlow.type:
      return {
        ...state,
        loading: false,
        registrationFlow: 'complete',
        verificationFlow: (<any>action).response.continue_with
          ? action.response.continue_with[0]?.flow
          : undefined,
        auth: {
          session: (<any>action).response.session,
          userId: (<any>action).response.session?.identity?.metadata_public['ca_member_id'],
        },
      };

    case actions.FailCompleteRegistrationFlow.type:
      return { ...state, loading: false, registrationFlow: (<any>action).response };

    case actions.RequestGetVerificationFlow.type: {
      return { ...state, loading: true };
    }
    case actions.SuccessGetVerificationFlow.type: {
      return {
        ...state,
        loading: false,
        verificationFlow: (<any>action).flow,
      };
    }
    case actions.RequestUpdateVerificationFlow.type: {
      return { ...state, loading: true };
    }
    case actions.SuccessUpdateVerificationFlow.type: {
      return { ...state, loading: false };
    }
    case actions.FailUpdateVerificationFlow.type: {
      return { ...state, loading: false, verificationFlow: action.response };
    }

    case actions.RESET_STATE:
      return initialState;

    default:
      return state;
  }
}

export const createSelectors = (rootReducer: any) => ({
  getLoggedIn: createSelector(
    rootReducer,
    (state: State): boolean =>
      state.auth &&
      ((<KratosAuth>state.auth).session?.active === true ||
        typeof (<LoopbackAuth>state.auth).id === 'string')
  ),
  getAuth: createSelector(rootReducer, (state: State) => state.auth),
  getError: createSelector(rootReducer, (state: State) => state.error),
  getLoading: createSelector(rootReducer, (state: State) => state.loading),
  getPersisted: createSelector(rootReducer, (state: State) => {
    const persisted = { ...state };
    delete persisted.ready;
    delete persisted.loading;
    return persisted;
  }),
  getResetPassword: createSelector(rootReducer, (state: State) => state.resetPassword),
  getLoggedInUser: createSelector(rootReducer, (state: State) => state.user),
  getRoles: createSelector(rootReducer, (state: State) => state.roles),
  isAdmin: createSelector(
    rootReducer,
    (state: State) => state.roles && !!state.roles.find((value) => value === ADMIN_ROLE)
  ),
  isReady: createSelector(rootReducer, (state: State) => (state ? state.ready : false)),
  getLatestSeenReleaseNoteGuid: createSelector(rootReducer, (state: State) =>
    state ? state.user.meta['latestReleaseNoteSeen'] : null ?? null
  ),
  getAnnouncementPaneHintDismissed: createSelector(rootReducer, (state: State) =>
    state ? state.user.meta['announcementPaneHintDismissed'] : null ?? null
  ),
});
